import { useQpmCalcRentingCostDurationPutMutation } from "@/services/backend/qpm/calculations/api/calculation-api";
import t from "@/lang/lang";
import { fMoney } from "@/routes/processes/_components/ui/number-input";
import {
  RentingCost,
  UpdateRentingCostDurationRequest,
} from "@/services/backend/qpm/calculations/types/calculation";
import { useState } from "react";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/ui/collapsible";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Label } from "@/shared/components/ui/label";
import { RentingCostComponent } from "@/routes/processes/[processId]/qpm/calculations/[calculationId]/_components/renting-cost-component";
import { useDebouncedMutationWithPersistenceStateContextUpdate } from "@/shared/lib/debounce/debounce";
import { IntNumberInput } from "@/shared/components/domain/qpm/inputs/number-input";
import { useCalculationData } from "@/services/backend/qpm/calculations/hooks/calculation-context";

export function RentalRoute() {
  const { calculation } = useCalculationData();

  return (
    calculation && (
      <div className="overflow-clip rounded-lg border shadow-sm">
        <RentingCostCollapsible
          calculationId={calculation.id}
          rentingCost={calculation.rentingCost}
        />
        <div className="flex justify-between rounded-b-lg border-t bg-gray-50 px-6 py-3">
          <span className="font-bold uppercase">
            {t("Kosten Funktionsprüfung pro Jahr")}
          </span>
          <span className="font-bold">
            {fMoney(calculation.rentingCost.maintenanceCost)}
          </span>
        </div>
        <div className="flex justify-between rounded-b-lg border-t bg-gray-50 px-6 py-3">
          <span className="font-bold uppercase">
            {t("Monatsrate inklusive Wartung")}
          </span>
          <span className="font-bold">
            {fMoney(calculation.rentingCost.ratePerMonthWithMaintenance)}
          </span>
        </div>
        <div className="flex justify-between rounded-b-lg border-t bg-gray-50 px-6 py-3">
          <span className="font-bold uppercase">
            {t("Gesamtpreis inklusive Wartung")}
          </span>
          <span className="font-bold">
            {fMoney(calculation.rentingCost.totalCostWithMaintenance)}
          </span>
        </div>
      </div>
    )
  );
}

export type RentingCostComponentProps = {
  calculationId: string;
  rentingCost: RentingCost;
};

function RentingCostCollapsible({
  calculationId,
  rentingCost,
}: RentingCostComponentProps) {
  const [open, setOpen] = useState(true);

  return (
    rentingCost && (
      <Collapsible open={open} onOpenChange={setOpen}>
        <div className="border-t bg-gray-50 px-6 py-3">
          <div className="flex justify-between font-bold">
            <CollapsibleTrigger>
              <div className="flex items-center space-x-2">
                {open ? (
                  <ChevronDown className="h-5 w-5" />
                ) : (
                  <ChevronRight className="h-5 w-5" />
                )}
                <span>{t("Mietkosten")}</span>
              </div>
            </CollapsibleTrigger>
            <span className="flex items-center justify-end gap-2">
              <RentingCostDurationComponent
                calculationId={calculationId}
                rentingCost={rentingCost}
              />
            </span>
            <span className="flex items-center justify-end gap-2">
              <Label
                htmlFor={`${rentingCost?.id}-renting-rate-per-month-input`}
              >
                {t("Monatsrate")}
              </Label>
              <span id={`${rentingCost?.id}-renting-rate-per-month-input`}>
                {fMoney(rentingCost.ratePerMonth)}
              </span>
            </span>
            <span className="flex items-center justify-end gap-2">
              <Label htmlFor={`${rentingCost?.id}-renting-total-cost-input`}>
                {t("Gesamt")}
              </Label>
              <span id={`${rentingCost?.id}-renting-total-cost-input`}>
                {fMoney(rentingCost.totalCost)}
              </span>
            </span>
          </div>
        </div>
        <CollapsibleContent>
          <div className="border-t">
            <RentingCostComponent
              calculationId={calculationId}
              rentingCost={rentingCost}
            />
          </div>
          {/* <RentingGeneralSettingsComponent */}
          {/*   rentingCost={rentingCost} */}
          {/*   calculationId={rentingCost.calculationValuesId} */}
          {/* /> */}
        </CollapsibleContent>
      </Collapsible>
    )
  );
}

export type RentingCostDurationComponentProps = {
  calculationId: string;
  rentingCost: RentingCost;
};

function RentingCostDurationComponent({
  calculationId,
  rentingCost,
}: RentingCostDurationComponentProps) {
  const [request, setRequest] = useState<UpdateRentingCostDurationRequest>({
    calculationId,
    rentingDuration: rentingCost.rentingDuration,
  });

  const [setDuration, { isLoading, error, reset, isSuccess }] =
    useQpmCalcRentingCostDurationPutMutation();

  useDebouncedMutationWithPersistenceStateContextUpdate(
    request,
    setDuration,
    isLoading,
    error,
    isSuccess,
    reset,
    500,
  );

  return (
    <>
      <Label htmlFor={`${rentingCost.id}-renting-duration-input`}>
        {t("Vertragslaufzeit (Monate)")}
      </Label>
      <IntNumberInput
        id={`${rentingCost.id}-renting-duration-input`}
        value={request.rentingDuration || 0}
        onChange={(val) => {
          const duration = val < 0 ? 0 : val;
          setRequest({ ...request, rentingDuration: duration });
        }}
      />
    </>
  );
}
